import { zodResolver } from '@hookform/resolvers/zod'
import { Checkbox, FormControlLabel, Popover } from '@material-ui/core'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'

import Button from '@/components/button'
import { useDownloadFilesFromDemand } from '@/hooks/mutations/use-download-files-from-demand'
import { Header } from '@/pages/diagnosis-view/components/header'
import { Shimmer } from '@/pages/diagnosis-view/components/shimmer'
import { useCreatePulverizationMap } from '@/pages/diagnosis-view/contexts/create-pulverization-map-context'
import { DownloadTypes } from '@/services/get-url-to-download-file'

import Info from '../info'
import { SummaryTable } from '../summary-table'
import { useStyles } from './styles'

const downloadFilesSchema = z.object({
  infestation: z.boolean().default(false),
  infestation_splitted: z.boolean().default(false),
  infestation_pdf: z.boolean().default(false),
  infestation_splitted_pdf: z.boolean().default(false),
  infestation_csv: z.boolean().default(false),
  perimeter: z.boolean().default(false),
  mosaic: z.boolean().default(false),
})

type DownloadFilesFormData = z.infer<typeof downloadFilesSchema>

export function InfestationSummary() {
  const navigate = useNavigate()
  const classes = useStyles()

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const { handleSubmit, control, watch, reset } =
    useForm<DownloadFilesFormData>({
      resolver: zodResolver(downloadFilesSchema),
    })

  const downloadFilesSelectedLength = Object.values(watch()).filter(
    Boolean,
  ).length

  const {
    hasPulverizationMap,
    setStep,
    nextStep,
    pulverizationData: pulverizationResume,
    infestationData,
    culture,
    diagnosisId,
  } = useCreatePulverizationMap()

  const { isLoading, mutate } = useDownloadFilesFromDemand(
    culture,
    diagnosisId!,
  )

  function onSubmitFilesDownloadForm(data: DownloadFilesFormData) {
    onCloseDownloadMenu()

    Object.entries(data)
      .filter(([, checked]) => checked)
      .forEach(([fileType], index) =>
        mutate({
          type: fileType as DownloadTypes,
          waitInMs: index * 1000,
        }),
      )
  }

  function onCloseDownloadMenu() {
    setAnchorEl(null)
    reset()
  }

  function handleNextStep() {
    if (hasPulverizationMap) return setStep(6)

    nextStep()
  }

  const isDisablePulverizationButton = useMemo(() => {
    if (hasPulverizationMap) return !pulverizationResume || !infestationData

    return !infestationData
  }, [hasPulverizationMap, pulverizationResume, infestationData])

  return (
    <>
      <Header title='Plantas daninhas' goBack={() => navigate(-1)} />

      <div className={classes.buttonContainer}>
        <Button
          onClick={handleNextStep}
          disabled={isDisablePulverizationButton}
        >
          <span className='material-symbols-outlined rotate'>sprinkler</span>
          {hasPulverizationMap ? 'Ver' : 'Criar'} mapa de pulverização
        </Button>
        <Button
          outlined
          loading={isLoading}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
            setAnchorEl(e.currentTarget)
          }
          disabled={!infestationData}
        >
          <span className='material-symbols-outlined'>download</span>
          Exportar arquivos
          <ArrowDropDownIcon />
        </Button>
      </div>

      {!infestationData ? (
        <>
          <Shimmer variant='box' maxSizeBox='initial' />
          <Shimmer variant='box' maxSizeBox='20rem' />
        </>
      ) : (
        <>
          <SummaryTable summary={infestationData.summary} />
          <Info
            title='Dados do diagnóstico'
            demandName={infestationData.demandName}
            flightDate={infestationData.flightDateLabel}
            concludedAt={infestationData.concludedAtLabel}
            gsd={infestationData.gsd}
            datum={infestationData.datum}
            totalArea={infestationData?.totalArea.ha}
          />
        </>
      )}
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={!!anchorEl}
        onClose={onCloseDownloadMenu}
        classes={{ paper: classes.paper }}
      >
        <form onSubmit={handleSubmit(onSubmitFilesDownloadForm)}>
          <Controller
            control={control}
            name='infestation'
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size='small' {...field} />}
                label='Arquivos de infestação (shapefile)'
              />
            )}
          />
          <Controller
            control={control}
            name='infestation_splitted'
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size='small' {...field} />}
                label='Arquivos de infestação por talhão (shapefile)'
              />
            )}
          />
          <Controller
            control={control}
            name='infestation_pdf'
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size='small' {...field} />}
                label='Mapa de infestação (PDF)'
              />
            )}
          />
          <Controller
            control={control}
            name='infestation_splitted_pdf'
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size='small' {...field} />}
                label='Mapa de infestação por talhão (PDF)'
              />
            )}
          />
          <Controller
            control={control}
            name='infestation_csv'
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size='small' {...field} />}
                label='Planilha de infestação'
              />
            )}
          />
          <Controller
            control={control}
            name='perimeter'
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size='small' {...field} />}
                label='Perímetro'
              />
            )}
          />
          <Controller
            control={control}
            name='mosaic'
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size='small' {...field} />}
                label='Ortomosaico'
              />
            )}
          />

          <Button type='submit' disabled={!downloadFilesSelectedLength}>
            Download{' '}
            {Boolean(downloadFilesSelectedLength) &&
              `(${downloadFilesSelectedLength} ${
                downloadFilesSelectedLength > 1 ? 'arquivos' : 'arquivo'
              })`}
          </Button>
        </form>
      </Popover>
    </>
  )
}
